import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';

const ACTIVE_SLEEP_COLLECTION = 'activeSleepSessions';
const ACTIVE_SLEEP_SESSION_SUFFIX = 'activeSleep';
const sleepLog = (message, details = {}) => {
  if (typeof console === 'undefined') {
    return;
  }

  console.info(`[sleep] ${message}`, details);
};

export const normalizeSleepType = (value = 'night') => (String(value).toLowerCase() === 'nap' ? 'nap' : 'night');

export const inferSleepTypeFromDateTime = (value) => {
  const date = coerceDate(value);
  if (!date) {
    return 'night';
  }

  const hour = date.getHours();
  return hour >= 6 && hour < 18 ? 'nap' : 'night';
};

export const inferSleepTypeFromWindow = (startTime, endTime) => {
  const start = coerceDate(startTime);
  const end = coerceDate(endTime);

  if (!start) {
    return 'night';
  }

  if (end) {
    if (start.toDateString() !== end.toDateString()) {
      return 'night';
    }

    const durationMin = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    return durationMin <= 180 ? 'nap' : 'night';
  }

  return inferSleepTypeFromDateTime(start);
};

export const getActiveSleepSessionId = (childId) => {
  if (!childId) {
    return '';
  }

  return `${childId}_${ACTIVE_SLEEP_SESSION_SUFFIX}`;
};

export const getActiveSleepSessionRef = (childId) => {
  const sessionId = getActiveSleepSessionId(childId);
  if (!sessionId) {
    return null;
  }

  return doc(db, ACTIVE_SLEEP_COLLECTION, sessionId);
};

const coerceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value?.toDate === 'function') {
    const resolved = value.toDate();
    return resolved instanceof Date && !Number.isNaN(resolved.getTime()) ? resolved : null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeSession = (snapshot, forcedId = null) => {
  if (!snapshot?.exists?.()) {
    return null;
  }

  const data = snapshot.data() || {};
  const startAt = data.startAt || data.startTime || null;
  const endAt = data.endAt || data.endTime || null;
  return {
    id: forcedId || snapshot.id,
    childId: data.childId || null,
    type: normalizeSleepType(data.type || 'night'),
    startAt,
    endAt,
    startTime: startAt,
    endTime: endAt,
    status: String(data.status || 'active').toLowerCase(),
    notes: data.notes || '',
    quality: data.quality || '',
    disturbances: data.disturbances || '',
    mediaUrls: Array.isArray(data.mediaUrls) ? data.mediaUrls : [],
    lastUpdated: data.lastUpdated || null,
  };
};

const isActiveSessionRecord = (session) => {
  if (!session) {
    return false;
  }

  const status = String(session.status || '').toLowerCase();
  const endTime = session.endTime ?? session.endAt ?? null;
  return Boolean(
    session.childId
    && session.startTime
    && status === 'active'
    && (endTime == null || endTime === '')
  );
};

const buildCanonicalSession = (session, canonicalId) => {
  if (!session) {
    return null;
  }

  const startTime = session.startTime || session.startAt || null;
  const endTime = session.endTime || session.endAt || null;

  return {
    id: canonicalId,
    childId: session.childId || null,
    type: normalizeSleepType(session.type || 'night'),
    startAt: startTime,
    startTime,
    endAt: endTime,
    endTime,
    status: 'active',
    notes: session.notes || '',
    quality: session.quality || '',
    disturbances: session.disturbances || '',
    mediaUrls: Array.isArray(session.mediaUrls) ? session.mediaUrls : [],
    lastUpdated: new Date().toISOString(),
  };
};

const listActiveSleepSessionsByChildId = async (childId) => {
  if (!childId) {
    return [];
  }

  const sessionsQuery = query(
    collection(db, ACTIVE_SLEEP_COLLECTION),
    where('childId', '==', childId),
  );

  sleepLog('active sleep collection query', {
    childId,
    collection: ACTIVE_SLEEP_COLLECTION,
    where: [
      { field: 'childId', op: '==', value: childId },
    ],
  });

  const snapshot = await getDocs(sessionsQuery);
  return snapshot.docs
    .map((docSnap) => normalizeSession(docSnap, docSnap.id))
    .filter((session) => isActiveSessionRecord(session));
};

const pruneNonCanonicalActiveSleepSessions = async (childId, canonicalSessionId) => {
  const sessions = await listActiveSleepSessionsByChildId(childId);
  await Promise.all(sessions
    .filter((session) => session.id !== canonicalSessionId)
    .map(async (session) => {
      try {
        await deleteDoc(doc(db, ACTIVE_SLEEP_COLLECTION, session.id));
        sleepLog('deleted non-canonical active sleep session', {
          childId,
          canonicalSessionId,
          deletedSessionId: session.id,
        });
      } catch (error) {
        sleepLog('failed to delete non-canonical active sleep session', {
          childId,
          canonicalSessionId,
          deletedSessionId: session.id,
          error: String(error?.message || error || ''),
        });
      }
    }));
};

export const getActiveSleepSession = async (childId) => {
  const ref = getActiveSleepSessionRef(childId);
  if (!ref) {
    sleepLog('getActiveSleepSession skipped because childId is missing', { childId });
    return null;
  }

  const trySnapshot = async (targetRef) => {
    try {
      const snapshot = await getDoc(targetRef);
      const normalized = snapshot.exists() ? normalizeSession(snapshot, targetRef.id) : null;
      sleepLog('active sleep doc read', {
        childId,
        path: `${ACTIVE_SLEEP_COLLECTION}/${targetRef.id}`,
        exists: snapshot.exists(),
        session: normalized
          ? {
            id: normalized.id,
            childId: normalized.childId,
            startTime: normalized.startTime,
            endTime: normalized.endTime,
            status: normalized.status,
          }
          : null,
      });
      return isActiveSessionRecord(normalized) ? normalized : null;
    } catch (error) {
      const message = String(error?.message || error || '').toLowerCase();
      sleepLog('active sleep doc read failed', {
        childId,
        path: `${ACTIVE_SLEEP_COLLECTION}/${targetRef.id}`,
        error: message,
      });
      if (!message.includes('permission')) {
        throw error;
      }
      return null;
    }
  };

  try {
    const canonical = await trySnapshot(ref);
    if (canonical) {
      sleepLog('resolved active sleep session', {
        childId,
        resolvedActiveSessionId: canonical.id,
        fallbackUsed: false,
        autoHealTriggered: false,
      });
      return canonical;
    }

    const activeSessions = await listActiveSleepSessionsByChildId(childId);
    if (activeSessions.length === 0) {
      sleepLog('resolved active sleep session', {
        childId,
        resolvedActiveSessionId: null,
        fallbackUsed: true,
        autoHealTriggered: false,
      });
      return null;
    }

    activeSessions.sort((a, b) => new Date(b.lastUpdated || b.startTime || 0) - new Date(a.lastUpdated || a.startTime || 0));
    const resolvedSession = activeSessions[0];
    const canonicalSession = buildCanonicalSession(resolvedSession, getActiveSleepSessionId(childId));
    const autoHealTriggered = resolvedSession.id !== canonicalSession.id;

    if (autoHealTriggered) {
      await setDoc(ref, canonicalSession);
      await deleteDoc(doc(db, ACTIVE_SLEEP_COLLECTION, resolvedSession.id));
      sleepLog('auto-healed active sleep session', {
        childId,
        sourceSessionId: resolvedSession.id,
        canonicalSessionId: canonicalSession.id,
      });
    }

    await pruneNonCanonicalActiveSleepSessions(childId, canonicalSession.id);
    sleepLog('resolved active sleep session', {
      childId,
      resolvedActiveSessionId: canonicalSession.id,
      fallbackUsed: true,
      autoHealTriggered,
    });
    return canonicalSession;
  } catch (error) {
    sleepLog('active sleep read fallback due to unexpected error', {
      childId,
      error: String(error?.message || error || ''),
    });
    return null;
  }
};

export const resumeSleep = async (childId) => {
  if (!childId) {
    return null;
  }

  return getActiveSleepSession(childId);
};

export const startSleep = async (childId, type = 'night', updates = {}) => {
  if (!childId) {
    throw new Error('Child is required to start sleep.');
  }

  const sessionType = normalizeSleepType(type);
  const existing = await getActiveSleepSession(childId);
  if (existing && normalizeSleepType(existing.type) !== sessionType) {
    throw new Error('Sleep is already active. End it before starting a different sleep mode.');
  }

  const ref = getActiveSleepSessionRef(childId);
  if (!ref) {
    throw new Error('Unable to resolve active sleep session.');
  }

  const now = new Date();
  const sessionId = getActiveSleepSessionId(childId);
  const startAt = coerceDate(updates.startAt || updates.startTime || existing?.startAt || existing?.startTime || now) || now;
  const nextSession = {
    id: sessionId,
    childId,
    type: sessionType,
    startAt: startAt.toISOString(),
    startTime: startAt.toISOString(),
    endAt: null,
    endTime: null,
    status: 'active',
    notes: updates.notes ?? existing?.notes ?? '',
    title: updates.title ?? existing?.title ?? '',
    titlePrefix: updates.titlePrefix ?? existing?.titlePrefix ?? '',
    quality: updates.quality ?? existing?.quality ?? '',
    disturbances: updates.disturbances ?? existing?.disturbances ?? '',
    mediaUrls: Array.isArray(updates.mediaUrls)
      ? updates.mediaUrls
      : (Array.isArray(existing?.mediaUrls) ? existing.mediaUrls : []),
    lastUpdated: now.toISOString(),
  };

  sleepLog('starting active sleep session', {
    childId,
    path: `${ACTIVE_SLEEP_COLLECTION}/${sessionId}`,
    payload: nextSession,
  });
  await setDoc(ref, nextSession);

  await pruneNonCanonicalActiveSleepSessions(childId, sessionId);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('captureez:active-sleep-refresh', {
      detail: { childId, type: sessionType },
    }));
  }

  return nextSession;
};

export const updateActiveSleep = async (childId, type = 'night', updates = {}) => {
  if (!childId) {
    throw new Error('Child is required to update sleep.');
  }

  const sessionType = normalizeSleepType(type);
  const ref = getActiveSleepSessionRef(childId);
  if (!ref) {
    throw new Error('Unable to resolve active sleep session.');
  }

  const existing = await getActiveSleepSession(childId);
  if (!existing) {
    throw new Error('No active sleep session found.');
  }

  if (normalizeSleepType(existing.type) !== sessionType) {
    throw new Error('Sleep is already active. End it before switching modes.');
  }

  const resolvedStartAt = coerceDate(updates.startAt || updates.startTime || existing?.startAt || existing?.startTime || null);
  const nextSession = {
    ...existing,
    id: getActiveSleepSessionId(childId),
    ...updates,
    childId,
    type: sessionType,
    startAt: resolvedStartAt ? resolvedStartAt.toISOString() : existing?.startAt || existing?.startTime || null,
    startTime: resolvedStartAt ? resolvedStartAt.toISOString() : existing?.startAt || existing?.startTime || null,
    endAt: null,
    endTime: null,
    status: 'active',
    lastUpdated: new Date().toISOString(),
  };

  sleepLog('updating active sleep session', {
    childId,
    path: `${ACTIVE_SLEEP_COLLECTION}/${getActiveSleepSessionId(childId)}`,
    payload: nextSession,
  });
  await setDoc(ref, nextSession);

  await pruneNonCanonicalActiveSleepSessions(childId, getActiveSleepSessionId(childId));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('captureez:active-sleep-refresh', {
      detail: { childId, type: sessionType },
    }));
  }
  return nextSession;
};

export const endSleep = async (childId, type = 'night', updates = {}) => {
  if (!childId) {
    throw new Error('Child is required to end sleep.');
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in to end sleep.');
  }

  const sessionType = normalizeSleepType(type);
  const ref = getActiveSleepSessionRef(childId);
  if (!ref) {
    throw new Error('Unable to resolve active sleep session.');
  }

  const activeSession = await getActiveSleepSession(childId);
  if (!activeSession) {
    throw new Error('No active sleep session found.');
  }

  if (type && normalizeSleepType(activeSession.type) !== sessionType) {
    throw new Error('The active sleep session is using a different mode.');
  }

  const startTime = coerceDate(activeSession.startAt || activeSession.startTime) || new Date();
  const endTime = coerceDate(updates.endAt || updates.endTime) || new Date();
  const resolvedEndTime = endTime <= startTime
    ? new Date(startTime.getTime() + 24 * 60 * 60 * 1000)
    : endTime;
  const durationMin = Math.max(0, Math.round((resolvedEndTime - startTime) / 60000));
  const sleepDetails = {
    type: sessionType,
    status: 'completed',
    startAt: startTime.toISOString(),
    endAt: resolvedEndTime.toISOString(),
    startTime: startTime.toISOString(),
    endTime: resolvedEndTime.toISOString(),
    bedtime: startTime.toISOString(),
    wakeTime: resolvedEndTime.toISOString(),
    duration: durationMin,
    quality: updates.quality ?? activeSession.quality ?? '',
    disturbances: updates.disturbances ?? activeSession.disturbances ?? '',
    notes: updates.notes ?? activeSession.notes ?? '',
    mediaUrls: updates.mediaUrls ?? activeSession.mediaUrls ?? [],
  };

  sleepLog('ending active sleep session', {
    childId,
    path: `${ACTIVE_SLEEP_COLLECTION}/${getActiveSleepSessionId(childId)}`,
    activeSession: {
      id: activeSession.id,
      childId: activeSession.childId,
      startTime: activeSession.startTime,
      endTime: activeSession.endTime,
      status: activeSession.status,
    },
    payload: sleepDetails,
  });
  const completedDoc = {
    childId,
    category: 'sleep',
    status: 'completed',
    timestamp: resolvedEndTime,
    displayDate: resolvedEndTime,
    anchorDate: startTime,
    localDate: resolvedEndTime.toLocaleDateString('en-CA'),
    entryDate: resolvedEndTime.toDateString(),
    timestampSource: 'sleep-complete',
    text: updates.text || updates.notes || activeSession.notes || 'Sleep completed',
    title: updates.title || activeSession.title || 'Sleep',
    titlePrefix: updates.titlePrefix || activeSession.titlePrefix || 'Sleep',
    createdBy: currentUser.uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    mediaUrls: updates.mediaUrls ?? activeSession.mediaUrls ?? [],
    startAt: startTime.toISOString(),
    endAt: resolvedEndTime.toISOString(),
    startTime: startTime.toISOString(),
    endTime: resolvedEndTime.toISOString(),
    sleepDetails,
    source: 'sleep_log',
  };

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[sleep] writing completed dailyLogs entry', {
      childId,
      type: sessionType,
      completedDoc,
    });
  }

  const completedRef = await addDoc(collection(db, 'dailyLogs'), completedDoc);
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[sleep] completed dailyLogs write success', {
      childId,
      type: sessionType,
      dailyLogId: completedRef.id,
    });
  }
  await deleteDoc(ref);
  await pruneNonCanonicalActiveSleepSessions(childId, getActiveSleepSessionId(childId));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
      detail: {
        childId,
        id: completedRef.id,
        collection: 'dailyLogs',
        category: 'sleep',
        status: 'completed',
      },
    }));
    window.dispatchEvent(new CustomEvent('captureez:active-sleep-refresh', {
      detail: { childId, type: sessionType },
    }));
  }

  return {
    id: completedRef.id,
    ...completedDoc,
  };
};

export const discardSleep = async (childId, type = 'night') => {
  if (!childId) {
    throw new Error('Child is required to discard sleep.');
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in to discard sleep.');
  }

  const sessionType = normalizeSleepType(type);
  const ref = getActiveSleepSessionRef(childId);
  if (!ref) {
    return null;
  }

  const activeSession = await getActiveSleepSession(childId);
  if (!activeSession) {
    sleepLog('discard requested but no active session found', {
      childId,
      path: `${ACTIVE_SLEEP_COLLECTION}/${getActiveSleepSessionId(childId)}`,
    });
    return null;
  }

  if (type && normalizeSleepType(activeSession.type) !== sessionType) {
    throw new Error('The active sleep session is using a different mode.');
  }

  await deleteDoc(ref);
  await pruneNonCanonicalActiveSleepSessions(childId, getActiveSleepSessionId(childId));
  sleepLog('discarded active sleep session', {
    childId,
    path: `${ACTIVE_SLEEP_COLLECTION}/${getActiveSleepSessionId(childId)}`,
    session: {
      id: activeSession.id,
      childId: activeSession.childId,
      startTime: activeSession.startTime,
      endTime: activeSession.endTime,
      status: activeSession.status,
    },
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
      detail: {
        childId,
        collection: 'activeSleepSessions',
        category: 'sleep',
        status: 'discarded',
      },
    }));
    window.dispatchEvent(new CustomEvent('captureez:active-sleep-refresh', {
      detail: { childId, type: sessionType },
    }));
  }

  return true;
};
